import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, signal } from '@angular/core';
import { DebugService } from '@core/services/debug/debug.service';
import {
  PalantirDialogComponent,
  PalantirTextComponent,
  PalantirCheckboxComponent,
} from '@flux/design-system';
import { GetPermissionsGQL, Permissions } from '@generated/graphql';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-override-permissions',
  imports: [
    CommonModule,
    PalantirDialogComponent,
    PalantirTextComponent,
    PalantirCheckboxComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './override-permissions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverridePermissionsComponent {
  public readonly permissions = signal<Permissions[]>([]);

  private readonly checkedPermissions = new Set<string>();

  constructor(
    private readonly debugService: DebugService,
    private readonly getPermissionsGQL: GetPermissionsGQL,
  ) {
    this.getPermissions();

    const overridedPermissions =
      debugService.getSingleDebugMetadata<string[]>('overridedPermissions');

    if (overridedPermissions) {
      this.checkedPermissions = new Set(overridedPermissions);
    }
  }

  async getPermissions() {
    const permissions = await firstValueFrom(
      this.getPermissionsGQL.fetch({
        where: {
          parentId: null,
        },
      }),
    );
    this.permissions.set(permissions.data.permissions as Permissions[]);
  }

  public toggleAll(parent: Permissions) {
    const permissions = parent.children ?? [];

    if (permissions.length === 0) {
      return;
    }

    const isAllChecked = this.isAllChecked(parent);

    for (const permission of permissions) {
      if (isAllChecked) {
        this.checkedPermissions.delete(permission.id);
        continue;
      }

      this.checkedPermissions.add(permission.id);
    }

    this.debugService.setDebugMetadata('overridedPermissions', Array.from(this.checkedPermissions));
  }

  public togglePermission(permission: Permissions) {
    if (this.checkedPermissions.has(permission.id)) {
      this.checkedPermissions.delete(permission.id);
      return;
    }

    this.checkedPermissions.add(permission.id);

    this.debugService.setDebugMetadata('overridedPermissions', Array.from(this.checkedPermissions));
  }

  public isSelected(permission: Permissions) {
    return this.checkedPermissions.has(permission.id);
  }

  public isSomeChecked(parent: Permissions) {
    const permissions = parent.children ?? [];

    if (permissions.length === 0) {
      return false;
    }

    return (
      permissions.some((permission) => this.checkedPermissions.has(permission.id)) &&
      !this.isAllChecked(parent)
    );
  }

  public isAllChecked(parent: Permissions) {
    const permissions = parent.children ?? [];

    if (permissions.length === 0) {
      return false;
    }

    return permissions.every((permission) => this.checkedPermissions.has(permission.id));
  }
}
